import { searchHotelApi, getHotelDetailApi } from '../../services/api/search.service';
import { carSearchType } from '../../constants/Enums';
import { openAlert } from './alert';
import to from '../../utils/to';
import i18n from '../../i18n';
import * as RootNavigation from '../../utils/navigationRef';
import {
  selectActiveComponentIndex,
  selectTripResults,
  selectFirstSearchCriteria,
} from '../selectors/search.selector';
import {
  selectHotelSearchResults,
  selectSelectedHotel,
  selectActiveHotelComponent,
  selectNeighbourhoodList,
  selectHotelListFiltered,
} from '../selectors/hotel.selector';
import { orderByHotelList } from '../../constants/orderByLists';
import { getHotellistData, getFilterByDetails } from '../../utils/hotel.utils';
import { logException } from '../../utils/logger.utils';
import { selectAccessToken } from '../selectors/auth.selector';
import { Screens } from '../../constants/ScreenNames';
import { evaluateSearch, searchStatus } from '../../utils/search.utils';

export const SET_HOTEL_RESULTS = 'SET_HOTEL_RESULTS';
export const SELECT_HOTEL = 'SELECT_HOTEL';
export const SET_HOTEL_DETAIL = 'SET_HOTEL_DETAIL';
export const SELECT_HOTEL_DETAIL = 'SELECT_HOTEL_DETAIL';
export const SET_FILTER_BY_SEARCH_TYPE = 'SET_FILTER_BY_SEARCH_TYPE';
export const SET_FILTER_BY_DETAILS = 'SET_FILTER_BY_DETAILS';
export const ORDER_HOTEL_BY = 'ORDER_HOTEL_BY';
export const ORDER_HOTEL_BY_REVERSE = 'ORDER_HOTEL_BY_REVERSE';
export const FILTER_HOTEL_BY_TYPE = 'FILTER_HOTEL_BY_TYPE';
export const FILTER_HOTEL_BY_NAME = 'FILTER_HOTEL_BY_NAME';
export const CLEAR_HOTEL = 'CLEAR_HOTEL';

export const selectHotel = (selectedHotel) => {
  return async (dispatch, getState) => {
    const displayIndex = selectActiveComponentIndex(getState());
    dispatch({
      type: SELECT_HOTEL,
      selectedHotel,
      displayIndex,
    });
  };
};

export const selectHotelDetail = (selectedHotelDetail) => {
  return async (dispatch, getState) => {
    const displayIndex = selectActiveComponentIndex(getState());
    const searchResults = selectHotelSearchResults(getState());
    const lowestPrice = searchResults?.HotelFilterSummaryData?.MinPrice;
    dispatch({ type: SELECT_HOTEL_DETAIL, selectedHotelDetail, displayIndex, lowestPrice });
  };
};

export const filterByDetails = (obj) => {
  return async (dispatch, getState) => {
    const displayIndex = selectActiveComponentIndex(getState());
    dispatch({
      type: SET_FILTER_BY_DETAILS,
      displayIndex,
      filterByDetails: obj,
    });
  };
};

export const hotelSearch = (attempts = 0, isFirstAttempt = true, temporalData, index) => {
  return async (dispatch, getState) => {
    const route = RootNavigation.getCurrentRoute();
    const displayIndex = selectActiveComponentIndex(getState());
    if (Screens.Hotel !== route?.name || (typeof index === 'number' && index !== displayIndex)) {
      return;
    }
    const accessToken = selectAccessToken(getState());
    const results = selectTripResults(getState());
    const storeSearchResults = selectHotelSearchResults(getState());
    const travelRequestId = results.TravelRequestId;
    const requestedComponents = results.RequestedComponents;
    const hotelRequestedComponent = requestedComponents.find(
      (x) => x.DisplayIndex === displayIndex
    );
    const hotelComponentRequestId = hotelRequestedComponent?.ComponentRequestId;
    let [err, hotelSearchResults] = await to(
      searchHotelApi(accessToken, travelRequestId, hotelComponentRequestId)
    );
    const hotelListData = hotelSearchResults?.HotelListData;

    if (err) {
      logException(err, { metaData: { travelRequestId, hotelComponentRequestId } });
      if (attempts === 0) {
        dispatch(
          openAlert({
            title: i18n.t('HotelSearchFailureMessage'),
            confirmText: i18n.t('ReSearch'),
            onConfirmPressed: () => RootNavigation.navigate('Home'),
          })
        );
        return;
      }
    }

    const evaluation = evaluateSearch({
      isFirstAttempt,
      attempts,
      newResults: temporalData?.HotelListData,
      initialResults: storeSearchResults?.HotelListData,
      currentResults: hotelSearchResults?.HotelListData,
    });

    switch (evaluation) {
      case searchStatus.INCOMPLETE:
        dispatch(hotelSearch(attempts - 1, false, hotelSearchResults, index));
        return;
      case searchStatus.COMPLETE:
        hotelSearchResults = temporalData;
        break;
      case searchStatus.CANCELED:
        return;
      case searchStatus.PARTIAL:
      default:
        break;
    }

    let list = [];
    if (hotelListData) {
      list = getHotellistData(
        hotelListData,
        hotelSearchResults?.HotelFilterSummaryData?.NeighbourhoodListWithHotelIds
      );
    }

    const categorySelected = selectFirstSearchCriteria(getState())?.category;

    dispatch({
      type: SET_HOTEL_RESULTS,
      searchResults: {
        ...hotelSearchResults,
        HotelListData: list,
      },
      displayIndex,
      searchType:
        list.length > 0
          ? categorySelected == 'Airports'
            ? carSearchType.AIRPORT.toLowerCase()
            : carSearchType.CITY.toLowerCase()
          : null,
    });

    const responses = selectHotelListFiltered(getState());
    const neighbourhoodList = selectNeighbourhoodList(getState());
    const _filterByDetails = getFilterByDetails(responses, neighbourhoodList);

    dispatch(filterByDetails(_filterByDetails));

    if (isFirstAttempt && attempts > 0) {
      dispatch(hotelSearch(attempts - 1, false, hotelSearchResults, displayIndex));
    }
  };
};

export const getHotelDetail = () => {
  return async (dispatch, getState) => {
    const state = getState();
    const displayIndex = selectActiveComponentIndex(state);
    const accessToken = selectAccessToken(state);
    const searchResults = selectHotelSearchResults(state);
    const lowestPrice = searchResults?.HotelFilterSummaryData.MinPrice;
    const tripResults = selectTripResults(state);
    const travelRequestId = tripResults.TravelRequestId;
    const requestedComponents = tripResults.RequestedComponents;
    const hotelRequestedComponent = requestedComponents.find((x) => x.ComponentName === 'hotel');
    const hotelComponentRequestId =
      hotelRequestedComponent && hotelRequestedComponent.ComponentRequestId;
    const selectedHotel = selectSelectedHotel(state);

    const hotelDetailRequest = {
      StrHotelResponseId: selectedHotel.HotelResponseId,
      HotelId: selectedHotel.HotelId,
      TravelRequestId: travelRequestId,
      DisplayPrice: selectedHotel.DisplayPrice,
      CrowdPrice: selectedHotel.CrowdRate,
      IsPromo: selectedHotel.IsPromo,
      StrikeOutPrice: selectedHotel.AverageBaseCost,
      IsTmu: false,
      ImageUrl: selectedHotel.HotelImage,
      ChildAge: '',
      StarRating: selectedHotel.StarRating,
      LowestPrice: lowestPrice,
      HotelRequestId: hotelComponentRequestId,
    };
    const [err, hotelDetail] = await to(getHotelDetailApi(accessToken, hotelDetailRequest));
    if (err) {
      dispatch(
        openAlert({
          title: i18n.t('HotelDetailFailureMessage'),
          confirmText: i18n.t('GoBack'),
          onConfirmPressed: () => RootNavigation.goBack(),
        })
      );
      logException(err, { metaData: { travelRequestId, hotelComponentRequestId } });
    } else {
      dispatch({
        type: SET_HOTEL_DETAIL,
        hotelDetail,
        displayIndex,
      });
    }
  };
};

export const filterBySearchType = (searchType) => {
  return async (dispatch, getState) => {
    const displayIndex = selectActiveComponentIndex(getState());
    dispatch({
      type: SET_FILTER_BY_SEARCH_TYPE,
      displayIndex,
      searchType,
    });

    const responses = selectHotelListFiltered(getState());
    const neighbourhoodList = selectNeighbourhoodList(getState());
    const _filterByDetails = getFilterByDetails(responses, neighbourhoodList);

    dispatch(filterByDetails(_filterByDetails));
  };
};

export const orderBy = (key) => {
  return async (dispatch, getState) => {
    const displayIndex = selectActiveComponentIndex(getState());
    const { orderBy, reverse } = selectActiveHotelComponent(getState());

    if (orderBy === orderByHotelList[0].value && orderBy === key) {
      return;
    }
    if (orderBy === key) {
      dispatch({
        type: ORDER_HOTEL_BY_REVERSE,
        reverse: !reverse,
        displayIndex,
      });
    } else {
      dispatch({
        type: ORDER_HOTEL_BY,
        reverse: false,
        orderBy: key,
        displayIndex,
      });
    }
  };
};

export const filterBy = (filterBy) => {
  return async (dispatch, getState) => {
    const displayIndex = selectActiveComponentIndex(getState());
    dispatch({
      type: FILTER_HOTEL_BY_TYPE,
      filterBy,
      displayIndex,
    });
  };
};

export const filterByName = (filterByName) => {
  return async (dispatch, getState) => {
    const displayIndex = selectActiveComponentIndex(getState());
    dispatch({
      type: FILTER_HOTEL_BY_NAME,
      filterByName,
      displayIndex,
    });
  };
};

export const clearHotel = () => {
  return async (dispatch) => {
    dispatch({
      type: CLEAR_HOTEL,
      CLEAR_HOTEL,
    });
  };
};
